import React, { memo } from 'react';
import { ORG_CHART_HR_MAPPING } from 'routes/HumanResourcesModule/business/definitions';
import { START_DATE } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { useTranslation } from 'react-i18next';
import LoadingGrid from 'routes/HumanResourcesModule/components/LoadingGrid';
import { Box, Typography } from '@mui/material';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import _ from 'lodash';
import { UseQueryResult } from '@tanstack/react-query';
import { DetailedAgentArrivalHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/types';
import { DetailedAgentDepartureHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/types';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import Accordion from 'components/SharedComponents/Accordion/Accordion';

type Hypothesis =
    | DetailedAgentArrivalHypothesis
    | DetailedAgentDepartureHypothesis;

type Props<T extends Hypothesis> = {
    hrSimulation: HrSimulation;
    useGetData: (hrSimulationId: number) => UseQueryResult<T[], unknown>;
    titleKey: string;
    RecurrentGridComponent: React.ComponentType<{
        data: T[];
    }>;
    NonRecurrentGridComponent?: React.ComponentType<{
        data: T[];
    }>;
};

const AgentHypothesesResume = <T extends Hypothesis>({
    hrSimulation,
    useGetData,
    titleKey,
    RecurrentGridComponent,
    NonRecurrentGridComponent,
}: Props<T>): React.ReactElement => {
    const { t } = useTranslation();
    const { id: simulationId } = hrSimulation;

    const {
        data: detailedAgentHypothesesResponse,
        isLoading: isAgentHypothesesResponseLoading,
    } = useGetData(simulationId);

    const detailedAgentHypothesesData =
        _.sortBy(detailedAgentHypothesesResponse, [
            ORG_CHART_HR_MAPPING,
            START_DATE,
        ]) || [];

    return (
        <Accordion title={t(titleKey)} defaultExpanded={false}>
            <Box sx={{ marginTop: '1rem' }}>
                {isAgentHypothesesResponseLoading ? (
                    <LoadingGrid />
                ) : (
                    <>
                        <Typography
                            variant="body1Bold"
                            sx={(theme) => ({
                                display: 'block',
                                marginBottom: '1rem',
                                color: theme.palette.darkGrey,
                            })}
                        >
                            {t(
                                `humanResources.simulation.agents.${RecurrenceEnum.RECURRENT}`,
                            )}
                        </Typography>
                        <RecurrentGridComponent
                            data={detailedAgentHypothesesData}
                        />
                        {NonRecurrentGridComponent !== undefined && (
                            <>
                                <Typography
                                    variant="body1Bold"
                                    sx={(theme) => ({
                                        display: 'block',
                                        margin: '1rem 0',
                                        color: theme.palette.darkGrey,
                                    })}
                                >
                                    {t(
                                        `humanResources.simulation.agents.${RecurrenceEnum.NON_RECURRENT}`,
                                    )}
                                </Typography>
                                <NonRecurrentGridComponent
                                    data={detailedAgentHypothesesData}
                                />
                            </>
                        )}
                    </>
                )}
            </Box>
        </Accordion>
    );
};

export default memo(AgentHypothesesResume);
